import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Badge } from '../ui/badge'
import { useToast } from '../../hooks/use-toast'
import { invalidateIssueQueries } from '../issues/DismissSuggestionDialog'

const STATUS_TONE: Record<string, string> = {
    open: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    in_progress: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
}

/**
 * Closes tickets through `POST /issues/bulk-close` (one uid or many), then
 * refreshes every issue query (list, stats, category cards). A ticket the
 * server skips (a proposal or claim closes with an outcome, an already closed
 * one...) is reported in a toast, never silently dropped.
 */
export function useCloseIssues(org: string, onDone?: () => void) {
    const { t } = useTranslation('issues')
    const queryClient = useQueryClient()
    const { toast } = useToast()

    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/issues/bulk-close', {
        onSuccess: (data) => {
            invalidateIssueQueries(queryClient)
            const skipped = (data?.results ?? []).filter((r) => r.outcome === 'skipped')
            if (skipped.length > 0) {
                const reasons = [...new Set(skipped.map((r) => t(`steering.skip_reason.${r.reason ?? 'not_found'}`)))]
                toast({
                    title: t('steering.close_skipped', { count: skipped.length }),
                    description: reasons.join(', '),
                    variant: 'destructive',
                })
            }
            onDone?.()
        },
        onError: () => {
            toast({ title: t('steering.close_error'), variant: 'destructive' })
        },
    })

    return {
        close: (uids: string[]) => mutation.mutate({ params: { path: { orgCode: org } }, body: { uids } }),
        pending: mutation.isPending,
    }
}

/**
 * The status pill of a ticket. Any non-closed status makes it a button that
 * closes that one ticket; a closed ticket keeps a plain label.
 */
export function CloseStatusBadge({
    org,
    uid,
    status,
    testId,
    stopPropagation = false,
}: {
    org: string
    uid: string
    status: string
    testId: string
    /** Inside a clickable row, the click must not select the row. */
    stopPropagation?: boolean
}) {
    const { t } = useTranslation('issues')
    const { close, pending } = useCloseIssues(org)
    const label = t(`status.${status}`, { defaultValue: status })
    const tone = STATUS_TONE[status] ?? 'text-muted-foreground'

    if (status === 'closed') {
        return (
            <Badge variant="outline" className={`text-[10px] ${tone}`} data-testid={testId}>
                {label}
            </Badge>
        )
    }

    return (
        <button
            type="button"
            disabled={pending}
            title={t('steering.close')}
            aria-label={`${label}: ${t('steering.close')}`}
            onClick={(e) => {
                if (stopPropagation) e.stopPropagation()
                close([uid])
            }}
            onKeyDown={(e) => {
                if (stopPropagation) e.stopPropagation()
            }}
            data-testid={testId}
            data-status={status}
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        >
            <Badge variant="outline" className={`text-[10px] gap-1 cursor-pointer hover:opacity-80 ${tone}`}>
                {pending && <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" />}
                {label}
            </Badge>
        </button>
    )
}
